import { Paper, Flex, Text, Stack, Badge, Divider, Loader, Center, Box } from "@mantine/core";

interface CreditsConsumedTabProps {
  transactions: {
    id: string;
    matchedCompanyGtid: string | null;
    reason: string;
    amount: number;
    createdAt: string | Date;
  }[];
  loading?: boolean;
}

const REASON_LABELS: Record<string, string> = {
  VERIFICATION_FEE: "Company Verification",
  ENGAGEMENT_FEE: "Collaboration",
  NDA_EXECUTION: "NDA Execution",
  MATCH_PAYMENT: "Collaboration",
};

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function CreditsConsumedTab({ transactions, loading }: CreditsConsumedTabProps) {
  if (loading) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  if (transactions.length === 0) {
    return (
      <Paper p="xl" radius="lg" withBorder>
        <Text c="slate.5" ta="center">
          No credit transactions yet
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="sm">
      {transactions.map((transaction) => (
        <Paper key={transaction.id} px="lg" py="md" radius="lg" style={{ border: "1px solid #E5E7EB" }}>
          {/* Desktop */}
          <Box visibleFrom="sm">
            {/* Row 1: Credits number + "Credits Consumed" | right: Reason */}
            <Flex justify="space-between" align="center" mb="sm">
              <Flex align="baseline" gap={8}>
                <Text fz={22} fw={400}>
                  {transaction.amount}
                </Text>
                <Text fz="sm" c="slate.6">
                  Credits Consumed
                </Text>
              </Flex>
              <Text fz="sm" c="green.7" fw={500}>
                {REASON_LABELS[transaction.reason] || transaction.reason}
              </Text>
            </Flex>

            {/* Row 2: Badge | divider | Date */}
            <Flex align="center" gap="md">
              <Badge
                variant="light"
                size="md"
                radius="sm"
                styles={{
                  root: {
                    textTransform: "none",
                    backgroundColor: "#EEF2F7",
                    color: "#374151",
                  },
                }}
              >
                {transaction.matchedCompanyGtid || "—"}
              </Badge>
              <Divider orientation="vertical" />
              <Text fz="sm" c="slate.6">
                {formatDate(transaction.createdAt)}
              </Text>
            </Flex>
          </Box>

          {/* Mobile */}
          <Box hiddenFrom="sm">
            {/* Row 1 */}
            <Flex justify="space-between" align="center" mb="sm">
              <Flex align="baseline" gap={6}>
                <Text fz={20} fw={400}>
                  {transaction.amount}
                </Text>
                <Text fz="xs" c="slate.6">
                  Credits Consumed
                </Text>
              </Flex>
              <Text fz="xs" c="green.7" fw={500}>
                {REASON_LABELS[transaction.reason] || transaction.reason}
              </Text>
            </Flex>

            {/* Row 2 */}
            <Flex align="center" gap="sm">
              <Badge
                variant="light"
                size="sm"
                radius="sm"
                styles={{
                  root: {
                    textTransform: "none",
                    backgroundColor: "#EEF2F7",
                    color: "#374151",
                  },
                }}
              >
                {transaction.matchedCompanyGtid || "—"}
              </Badge>
              <Divider orientation="vertical" />
              <Text fz="xs" c="slate.6">
                {formatDate(transaction.createdAt)}
              </Text>
            </Flex>
          </Box>
        </Paper>
      ))}
    </Stack>
  );
}
